<template>
    <vx-table-row>
        <vx-table-row-title>
            <div class="vx-flex vx-items-center vx-gap-2">
                <campaign-name :modelValue="props.modelValue"></campaign-name>
                <campaign-details-popover :modelValue="props.modelValue" :groupId="props.groupId"></campaign-details-popover>
            </div>
        </vx-table-row-title>

        <vx-table-row-item>
            <template v-slot:label> Status: </template>
            <vx-badge
                class="vx-mr-4"
                size="xs"
                :color="statusColor(props.modelValue.status).badge"
                data-test="campaign-status"
                >{{ generalStatusLabel(props.modelValue.status) }}</vx-badge
            >
        </vx-table-row-item>

        <vx-table-row-item data-test="contacts-count">
            <template v-slot:label> Contacts: </template>
            <font-awesome-icon
                v-if="loadingContactsCount"
                :icon="faLoader"
                spin
            ></font-awesome-icon>
            <template v-else>
                {{ vxFormatCount(contactsCount) }}
            </template>
        </vx-table-row-item>

        <vx-table-row-item data-test="contacts-count">
            <template v-slot:label> Send On: </template>
            <span class="vx-text-slate-400" data-test="live-at">
                {{ props.modelValue.live_at ? toShortDateTimeRelativeDays(props.modelValue.live_at, getClientTz(), false) : '-' }}
            </span>
        </vx-table-row-item>

        <vx-table-row-buttons v-if="!props.readOnly">
            <vx-button
                @click.prevent="destroy()"
                :disabled="teamStatus !== 'active'"
                :data-test="`delete-btn`"
                color="muted"
                outline
            >
                Delete <font-awesome-icon :icon="faFilePen" />
            </vx-button>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script setup>
import {
    faFilePen,
    faLoader,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import {
    VxBadge,
    VxButton,
    VxTableRow,
    VxTableRowItem,
    VxTableRowTitle,
    VxTableRowButtons,
    vxFormatCount,
    VxSwal,
} from "@voxie/frontend-components";
import { generalStatusLabel } from "~/components/filters";
import { ref } from "vue";
import campaignsClient from "~/services/campaignsClient";
import { statusColor } from "~/utils/campaigns";
import CampaignName from "./CampaignName.vue";
import CampaignDetailsPopover from "./CampaignDetailsPopover.vue";
import { getClientTz, toShortDateTimeRelativeDays } from "~/utils/date";
import { throwSwalRequestError } from "~/utils/request";

const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
    groupId: {
        type: String,
        default: undefined,
    },
    readOnly: {
        type: Boolean,
        default: false,
    }
});

const emit = defineEmits(["deleted"]);

const teamId = Spark.state.currentTeam.id;
const teamStatus = Spark.state.currentTeam.status;

const contactsCount = ref(null);
const loadingContactsCount = ref(false);
const fetchContactCounts = async () => {
    try {
        loadingContactsCount.value = true;
        const response = !props.groupId ?
            await campaignsClient.campaignsContactsCount(
                teamId,
                props.modelValue.id
            ) :
            await campaignsClient.groupCampaignsContactsCount(
                teamId,
                props.groupId,
                props.modelValue.id
            );

        contactsCount.value = response?.data?.count;

        loadingContactsCount.value = false;
    } catch (e) {
        console.error(e);
    }
};

fetchContactCounts();

const destroy = () => {
    VxSwal.confirmation(
        'Delete Campaign',
        "Are you sure? This campaign and it's messages will be deleted. There is no undo.",
        'danger',
        () =>
            (props.groupId ? campaignsClient.groupCampaignDelete(
                teamId,
                props.groupId,
                props.modelValue.id
            ) : campaignsClient.campaignDelete(
                teamId,
                props.modelValue.id
            ))
                .then(() => emit("deleted", props.modelValue.id))
                .catch(throwSwalRequestError)
    );
};
</script>
